import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../../api-client";
import type {
	CourtAvailSlotInput,
	CreateCourtAvailSource,
	UpdateCourtAvailSource,
} from "../../../schemas/court-availability";

/** Query key of the source list every mutation here invalidates. */
export const COURT_AVAIL_SOURCES_KEY = ["court-availability-sources"] as const;

export function useCourtAvailSources() {
	return useQuery({
		queryFn: () => adminApi.courtAvailability.listSources(),
		queryKey: COURT_AVAIL_SOURCES_KEY,
	});
}

/** Upcoming slots a source holds; only fetched while the inspector is open. */
export function useCourtAvailSlots(sourceId: string, enabled: boolean) {
	return useQuery({
		enabled,
		queryFn: () => adminApi.courtAvailability.listSlots(sourceId),
		queryKey: ["court-availability-slots", sourceId] as const,
	});
}

export function useCreateCourtAvailSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateCourtAvailSource) => adminApi.courtAvailability.createSource(data),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COURT_AVAIL_SOURCES_KEY }),
	});
}

export function useUpdateCourtAvailSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ sourceId, data }: { sourceId: string; data: UpdateCourtAvailSource }) =>
			adminApi.courtAvailability.updateSource(sourceId, data),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COURT_AVAIL_SOURCES_KEY }),
	});
}

export function useDeleteCourtAvailSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (sourceId: string) => adminApi.courtAvailability.deleteSource(sourceId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COURT_AVAIL_SOURCES_KEY }),
	});
}

/** Scrapes one source inline and returns what the run produced. */
export function useSyncCourtAvailSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (sourceId: string) => adminApi.courtAvailability.syncSource(sourceId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COURT_AVAIL_SOURCES_KEY }),
	});
}

export function useSyncAllCourtAvailSources() {
	return useMutation({ mutationFn: () => adminApi.courtAvailability.triggerSync() });
}

export function useIngestCourtSlots() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ sourceId, slots }: { sourceId: string; slots: CourtAvailSlotInput[] }) =>
			adminApi.courtAvailability.ingestSlots(sourceId, slots),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: COURT_AVAIL_SOURCES_KEY }),
	});
}
